// src/components/MobileHeader.jsx
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AuthService } from "../services/auth";
import ModalInscription from "./ModalInscription";
import AvatarSVG from "./AvatarSVG";
import NotificationsPanel from "./NotificationsPanel";
import { getUnreadCount } from "../services/notifications";

export default function MobileHeader({ user, showBack }) {
  const navigate = useNavigate();
  
  const [menuOpen, setMenuOpen] = useState(false);
  const [signupModalOpen, setSignupModalOpen] = useState(false);
  const [avatarDropdownOpen, setAvatarDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [busy, setBusy] = useState(false);
  
  const menuRef = useRef(null);
  const avatarRef = useRef(null);

  // Charger le nombre de notifications non lues
  useEffect(() => {
    loadUnreadCount();
  }, []);

  const loadUnreadCount = async () => {
    try {
      const count = await getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error("Erreur lors du chargement du nombre de notifications:", error);
    }
  };

  // Fermer les menus au clic extérieur
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && 
          avatarRef.current && !avatarRef.current.contains(e.target)) {
        setMenuOpen(false);
        setAvatarDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fermer les menus avec Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setAvatarDropdownOpen(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  const handleLogin = () => {
    setMenuOpen(false);
    navigate("/connexion");
  };

  const handleSignup = () => {
    setMenuOpen(false);
    setSignupModalOpen(true);
  };

  const handleProfile = () => {
    setAvatarDropdownOpen(false);
    navigate("/profil");
  };

  const handleLogout = async () => {
    try {
      setBusy(true);
      await AuthService.logout();
      setAvatarDropdownOpen(false);
      window.location.href = "/connexion";
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    } finally {
      setBusy(false);
    }
  };

  const userName = user?.name || "Utilisateur";

  return (
    <>
      <div className="md:hidden sticky top-0 z-40 bg-white/90 backdrop-blur border-b">
        <div className="h-14 container-padded flex items-center justify-between">
          {/* Logo / Back button à gauche */}
          <div className="flex-1">
            {showBack ? (
              <button 
                onClick={() => navigate(-1)} 
                aria-label="Retour" 
                className="text-blue-700"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            ) : (
              <span className="text-blue-700 font-semibold">2S-PAY</span>
            )}
          </div>

          {/* Cloche de notification au milieu */}
          <div className="flex-1 flex justify-center">
            <button 
              onClick={() => {
                setNotificationsOpen(true);
                loadUnreadCount(); // Recharger au clic
              }}
              aria-label="Notifications" 
              className="text-blue-700 relative"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 0 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-xs flex items-center justify-center px-1 font-semibold">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
          </div>

          {/* Burger menu ou Avatar à droite */}
          <div className="flex-1 flex justify-end">
            {user ? (
              // Avatar avec dropdown
              <div 
                className="relative" 
                ref={avatarRef}
                onMouseEnter={() => setAvatarDropdownOpen(true)}
                onMouseLeave={() => setAvatarDropdownOpen(false)}
              >
                <button
                  onClick={() => setAvatarDropdownOpen(!avatarDropdownOpen)}
                  className="flex items-center focus:outline-none"
                  aria-label="Menu utilisateur"
                  aria-expanded={avatarDropdownOpen}
                >
                  <AvatarSVG name={userName} className="w-8 h-8" />
                </button>

                {/* Dropdown menu */}
                <AnimatePresence>
                  {avatarDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50"
                      onMouseEnter={() => setAvatarDropdownOpen(true)}
                      onMouseLeave={() => setAvatarDropdownOpen(false)}
                    >
                      <div className="py-1">
                        <button
                          onClick={handleProfile}
                          className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-3 transition-colors"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <circle cx="12" cy="8" r="4" />
                            <path d="M4 20a8 8 0 0 1 16 0" />
                          </svg>
                          <span><b>Profil</b></span>
                        </button>
                        <button
                          onClick={handleLogout}
                          disabled={busy}
                          className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors disabled:opacity-60"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <path d="M9 16l-4-4 4-4" />
                            <path d="M5 12h11" />
                            <path d="M13 5h3a3 3 0 013 3v8a3 3 0 01-3 3h-3" />
                          </svg>
                          <span><b>{busy ? "Déconnexion..." : "Se déconnecter"}</b></span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              // Burger menu
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="text-blue-700 focus:outline-none"
                  aria-label="Menu"
                  aria-expanded={menuOpen}
                >
                  <svg 
                    viewBox="0 0 24 24" 
                    className="w-6 h-6" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                  >
                    {menuOpen ? (
                      <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" />
                    ) : (
                      <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
                    )}
                  </svg>
                </button>

                {/* Menu dropdown */}
                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50"
                    >
                      <div className="py-1">
                        <button
                          onClick={handleLogin}
                          className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-3 transition-colors"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
                            <path d="M10 17l5-5-5-5" />
                            <path d="M15 12H3" />
                          </svg>
                          <span><b>Se connecter</b></span>
                        </button>
                        <button
                          onClick={handleSignup}
                          className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-3 transition-colors"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M22 21v-2a4 4 0 00-3-3.5M16 3.13a4 4 0 010 7.75" />
                          </svg>
                          <span><b>S'inscrire</b></span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal d'inscription */}
      <ModalInscription open={signupModalOpen} onClose={() => setSignupModalOpen(false)} />

      {/* Panel des notifications */}
      <NotificationsPanel 
        open={notificationsOpen} 
        onClose={() => {
          setNotificationsOpen(false);
          loadUnreadCount(); // Recharger après fermeture
        }}
        onUpdateCount={loadUnreadCount}
      />
    </>
  );
}
